/** Wire contracts shared by Cai's browser and native clients. */
export const MAX_MESSAGE_LENGTH = 4000
/** The server refuses a message sent with more, so the composer stops there before uploading any. */
export const MAX_FILES_PER_MESSAGE = 5

export type CaiAuth = {
  csrf_token: string
  /** Signed in as staff; any other session reads as signed out here. */
  authenticated: boolean
  first_name: string
  last_name?: string
  email: string
  conversation_mode: 'SINGLE' | 'MULTIPLE'
}

export enum ChatRole {
  User = 'USER',
  Assistant = 'ASSISTANT',
}

export enum DocumentFileFormat {
  Pdf = 'PDF',
  Csv = 'CSV',
  Xlsx = 'XLSX',
  Png = 'PNG',
  Jpeg = 'JPEG',
}

export type SharedDocument = {
  id: string
  filename: string
  file_format: DocumentFileFormat
  /** Pages of a PDF, or sheets of a workbook; a CSV file is one sheet, a photo one page. */
  page_count: number
}
/** A file Cai created and delivered as its own message. */
export type GeneratedFile = SharedDocument & { download_url: string }

/** What a file card calls its format. */
export function fileFormatLabel(format: DocumentFileFormat): string {
  switch (format) {
    case DocumentFileFormat.Xlsx:
      return 'Excel'
    case DocumentFileFormat.Csv:
      return 'CSV'
    case DocumentFileFormat.Png:
    case DocumentFileFormat.Jpeg:
      return 'Photo'
    default:
      return 'PDF'
  }
}

/** documents on a question holds the files it was sent with; its content may be empty when it holds some. */
/** The scheduled task that asked a question, rather than the person; id is null once the task is deleted. */
export type ScheduledQuestion = { id: string | null; title: string }

export type ChatMessage = {
  id: string
  role: ChatRole
  content: string
  /** When it was sent, ISO 8601; absent from servers older than it. */
  date_created?: string
  file?: GeneratedFile
  documents?: SharedDocument[]
  scheduledTask?: ScheduledQuestion
}
/** message and documents are empty once the question is in the transcript. */
export type ActiveTurn = {
  turn_id: string
  message: string
  /** When the pending question was sent; absent once it is in the transcript, and from older servers. */
  date_created?: string
  documents?: SharedDocument[]
  scheduledTask?: ScheduledQuestion
}

/** A website sign-in paused until the person enters the code the website sent them. */
/** How the website said it sent its sign-in code. */
export enum WebsiteCodeSentBy {
  Text = 'TEXT',
  Email = 'EMAIL',
  Unknown = 'UNKNOWN',
}

export type WebsiteVerification = {
  id: string
  origin: string
  expires_at: string
  /** Absent from servers before Cai told the two apart. */
  sent_by?: WebsiteCodeSentBy
}

/** A message sent while Cai answered, waiting its turn. */
export type QueuedMessage = {
  id: string
  message: string
  documents: SharedDocument[]
}

export type ConversationSnapshot = {
  messages: ChatMessage[]
  next_before: number | null
  active_turn: ActiveTurn | null
  documents: SharedDocument[]
  website_verification?: WebsiteVerification | null
  /** The thread this page belongs to; absent from servers older than it. */
  conversation_id?: string | null
  /** In the order they will be asked; absent from servers older than it. */
  queued_messages?: QueuedMessage[]
}

export type CaiMemory = {
  id: string
  content: string
  kind: string
  date_created: string
  date_updated: string
}
